import React, { useEffect, useState } from "react";

const API_BASE = window.location.hostname === "localhost" ? "http://localhost:5000" : "";
const STORAGE_KEY = "nifty_support_page_settings";

function getSavedSettings() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}");
  } catch {
    return {};
  }
}

function saveSettings(data) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
}

function getRiskPlan(entry, supportLow) {
  const premium = Number(entry || 0);
  const low = Number(supportLow || 0);
  let quantity = 0;
  let slPoints = 0;

  if (premium >= 40 && premium <= 100) {
    quantity = 650;
    slPoints = 15;
  } else if (premium >= 25 && premium < 40) {
    quantity = 1040;
    slPoints = 10;
  } else if (premium >= 10 && premium < 25) {
    quantity = 1755;
    slPoints = 7;
  } else if (premium >= 5 && premium < 10) {
    quantity = 3510;
    slPoints = 5;
  }

  if (!quantity || !low) return null;

  const riskPoints = Math.max(0, premium - low + slPoints);

  return {
    quantity,
    slPoints,
    riskPoints: Number(riskPoints.toFixed(2)),
    totalLoss: Number((quantity * riskPoints).toFixed(2)),
  };
}

function getSupportStatus(row) {
  if (!row?.support) return null;

  const entry = Number(row.entry || 0);
  const supportLow = Number(row.support.supportLow || 0);
  const supportHigh = Number(row.support.supportHigh || 0);

  if (!entry || !supportLow || !supportHigh) return null;

  const distanceFromZone = entry > supportHigh ? entry - supportHigh : 0;

  if (entry >= supportLow && distanceFromZone <= 5) {
    return { label: "Buy Now", className: "text-green-300" };
  }

  if (distanceFromZone > 5 && distanceFromZone <= 15) {
    return { label: "Near SZ", className: "text-yellow-300" };
  }

  return { label: "Away SZ", className: "text-red-300" };
}

export default function NiftySupportPage() {
  const [activeTab, setActiveTab] = useState("NCE");
  const [showSideMenu, setShowSideMenu] = useState(false);

  const isSensexTab = activeTab === "SXCE" || activeTab === "SXPE";
  const activeMarket = isSensexTab ? "SENSEX" : "NIFTY";
  const activeSide = activeTab.endsWith("PE") ? "PE" : "CE";
  const activeApi = isSensexTab
    ? `${API_BASE}/api/sx-support`
    : `${API_BASE}/api/nifty-support`;

  const saved = getSavedSettings();

  const [clientId, setClientId] = useState(saved.clientId || "");
  const [accessToken, setAccessToken] = useState(saved.accessToken || "");
  const [interval, setIntervalValue] = useState(saved.interval || "1");
  const [premiumBatch, setPremiumBatch] = useState(saved.premiumBatch || "first");
  const [supportRefreshSeconds, setSupportRefreshSeconds] = useState(30);
  const [dataBySide, setDataBySide] = useState({ NCE: null, NPE: null, SXCE: null, SXPE: null });
  const [loadingBySide, setLoadingBySide] = useState({ NCE: false, NPE: false, SXCE: false, SXPE: false });
  const [errorBySide, setErrorBySide] = useState({ NCE: "", NPE: "", SXCE: "", SXPE: "" });
  const [runningBySide, setRunningBySide] = useState({ NCE: false, NPE: false, SXCE: false, SXPE: false });

  const [statusFilters, setStatusFilters] = useState({
    buyNow: true,
    near: true,
    away: true,
  });

  useEffect(() => {
    const next = getSavedSettings();
    setClientId(next.clientId || "");
    setAccessToken(next.accessToken || "");
    setIntervalValue(next.interval || "1");
    setPremiumBatch(next.premiumBatch || "first");
  }, []);

  useEffect(() => {
  const token = localStorage.getItem("ct_login_token");

  const loadSecretSupportSettings = async () => {
    try {
      if (!token) return;

      const res = await fetch(`${API_BASE}/api/login/admin/secret-settings`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = await res.json().catch(() => ({}));
      const settings = data.settings || {};

      const nextClientId = String(settings.clientId || "").trim();
      const nextAccessToken = String(settings.accessToken || "").trim();
      const seconds = Number(settings.supportRefreshSeconds || 30);

      if (nextClientId) setClientId(nextClientId);
      if (nextAccessToken) setAccessToken(nextAccessToken);
      setSupportRefreshSeconds(Math.max(5, seconds));
    } catch {
      setSupportRefreshSeconds(30);
    }
  };

  loadSecretSupportSettings();
}, []);

  useEffect(() => {
  saveSettings({ clientId, accessToken, interval, premiumBatch });
}, [clientId, accessToken, interval, premiumBatch]);

  // eslint-disable-next-line no-unused-vars
  const scan = async () => {
  const scanKey = activeTab;
  const scanSide = activeSide;

  if (!clientId || !accessToken) {
    setErrorBySide(prev => ({ ...prev, [scanKey]: "Enter Client ID and Access Token" }));
    return;
  }

  setLoadingBySide(prev => ({ ...prev, [scanKey]: true }));
  setErrorBySide(prev => ({ ...prev, [scanKey]: "" }));

  try {
    const res = await fetch(`${activeApi}/scan`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        clientId,
        accessToken,
        interval,
        premiumMin: 5,
        premiumMax: 100,
        maxPerSide: 15,
        premiumBatch,
        side: scanSide,
      }),
    });

    const text = await res.text();
    const json = JSON.parse(text);

    if (!res.ok || !json.success) {
      throw new Error(typeof json.error === "string" ? json.error : "Scan failed");
    }

    setDataBySide(prev => ({ ...prev, [scanKey]: json }));
  } catch (err) {
    setErrorBySide(prev => ({ ...prev, [scanKey]: err.message || "Scan failed" }));
  } finally {
    setLoadingBySide(prev => ({ ...prev, [scanKey]: false }));
  }
};


  const loadAutoStatus = async () => {
    try {
      const [niftyJson, sxJson] = await Promise.all([
        fetch(`${API_BASE}/api/nifty-support/auto/status`).then(res => res.json()).catch(() => null),
        fetch(`${API_BASE}/api/sx-support/auto/status`).then(res => res.json()).catch(() => null),
      ]);

      setRunningBySide({
        NCE: !!niftyJson?.CE?.running,
        NPE: !!niftyJson?.PE?.running,
        SXCE: !!sxJson?.CE?.running,
        SXPE: !!sxJson?.PE?.running,
      });

      setDataBySide(prev => ({
        NCE: niftyJson?.CE?.data || prev.NCE,
        NPE: niftyJson?.PE?.data || prev.NPE,
        SXCE: sxJson?.CE?.data || prev.SXCE,
        SXPE: sxJson?.PE?.data || prev.SXPE,
      }));

      setErrorBySide(prev => ({
        NCE: niftyJson?.CE?.error || prev.NCE,
        NPE: niftyJson?.PE?.error || prev.NPE,
        SXCE: sxJson?.CE?.error || prev.SXCE,
        SXPE: sxJson?.PE?.error || prev.SXPE,
      }));
    } catch {
      // keep old data if status check fails
    }
  };

const toggleAutoScan = async () => {
  const scanKey = activeTab;
  const scanSide = activeSide;

  if (!clientId || !accessToken) {
    setErrorBySide(prev => ({ ...prev, [scanKey]: "Enter Client ID and Access Token" }));
    return;
  }

  const action = runningBySide[scanKey] ? "stop" : "start";

  setRunningBySide(prev => ({
    ...prev,
    [activeTab]: action === "start",
  }));

  const res = await fetch(`${activeApi}/auto/${action}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      clientId,
      accessToken,
      interval,
      premiumMin: 5,
      premiumMax: 100,
      maxPerSide: 15,
      premiumBatch,
      refreshSeconds: supportRefreshSeconds,
      side: scanSide,
    }),
  });

  const json = await res.json();

  if (json.data) {
    setDataBySide(prev => ({ ...prev, [scanKey]: json.data }));
  }

  await loadAutoStatus();
};

useEffect(() => {
  loadAutoStatus();

  const timer = setInterval(() => {
    loadAutoStatus();
  }, 10000);

  return () => clearInterval(timer);
}, []);

  const toggleStatusFilter = key => {
    setStatusFilters(prev => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const shouldShowRow = row => {
    const status = getSupportStatus(row);

    if (!status) {
      return statusFilters.buyNow && statusFilters.near && statusFilters.away;
    }

    if (status.label === "Buy Now") return statusFilters.buyNow;
    if (status.label === "Near SZ") return statusFilters.near;
    if (status.label === "Away SZ") return statusFilters.away;

    return true;
  };

  const renderRows = rows => (
    <div className="space-y-2">
      {(rows || []).filter(shouldShowRow).map(row => {
        const status = getSupportStatus(row);

        return (
          <div
            key={`${row.side}-${row.strike}`}
            className="bg-[#111f33] border border-slate-700 rounded-lg p-3"
          >
            <div className="flex justify-between gap-3">
              <div className="font-bold">
                {row.side} {row.strike}
              </div>

              <div className="text-right">
                <div className="text-cyan-300 font-bold">
                  Entry Rs.{Number(row.entry || 0).toFixed(2)}
                </div>

                {status && (
                  <div className={`${status.className} text-xs font-bold mt-1`}>
                    {status.label}
                  </div>
                )}
              </div>
            </div>

            {row.support ? (
              <div className="mt-2 text-sm">
                <div className="text-green-300 font-semibold">
                  {row.support.type}: Rs.{row.support.supportLow} - Rs.{row.support.supportHigh}
                </div>

                <div className="text-red-300">
                  {(() => {
                    const risk = getRiskPlan(row.entry, row.support.supportLow);
                    const finalSl = risk ? Number(row.entry || 0) - risk.riskPoints : row.support.stopLoss;
                    const slPoints = risk ? risk.slPoints : row.support.slPoints;

                    return `SL: Rs.${Number(finalSl).toFixed(2)} (${slPoints} points below support)`;
                  })()}
                </div>

                {(() => {
                  const risk = getRiskPlan(row.entry, row.support.supportLow);
                  if (!risk) return null;

                  return (
                    <div className="text-yellow-300 mt-1">
                      Qty: {risk.quantity} | Risk: {risk.riskPoints} pts | SL: {risk.slPoints} pts | Loss: Rs.{Number(risk.totalLoss).toFixed(2)}
                    </div>
                  );
                })()}

                <div className="text-slate-400">
                  Touches: {row.support.touches} | Range: {row.support.range} pts | Candles: {row.candleCount || 0}
                </div>

                <div className={row.aligned ? "text-green-300" : "text-yellow-300"}>
                  {row.aligned ? "Aligned with market data" : "Not aligned / wait"}
                </div>
              </div>
            ) : (
              <div className="mt-2 text-sm text-slate-500">
                No clear support level/range found. Candles checked: {row.candleCount || 0}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );

  const data = dataBySide[activeTab];
  const loading = loadingBySide[activeTab];
  const error = errorBySide[activeTab];
  const rows = activeSide === "CE" ? data?.ce : data?.pe;

  return (
    <div className="min-h-screen bg-[#07111f] text-white">
      <div className="border-b border-slate-800 bg-[#0b1628] p-4">
        <div className="flex flex-wrap items-center gap-3">
          <h2 className="text-lg font-bold text-cyan-300 mr-2">Support Scanner</h2>

          <button
            type="button"
            onClick={() => setShowSideMenu(prev => !prev)}
            className="px-4 py-2 rounded bg-[#111f33] border border-slate-700 text-cyan-200 font-semibold hover:bg-[#172a46]"
          >
            NIFTY {showSideMenu ? "Hide" : "Show"}
          </button>

          {showSideMenu && (
            <div className="grid grid-cols-2 gap-2">
          {[
            { id: "NCE", label: "N CE", color: "bg-blue-500" },
            { id: "NPE", label: "N PE", color: "bg-pink-500" },
            { id: "SXCE", label: "SX CE", color: "bg-blue-500" },
            { id: "SXPE", label: "SX PE", color: "bg-pink-500" },
          ].map(tab => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={
                activeTab === tab.id
                  ? `px-3 py-2 rounded ${tab.color} font-semibold text-sm`
                  : "px-3 py-2 rounded hover:bg-[#111f33] text-sm"
              }
            >
              {tab.label}
            </button>
          ))}
        </div>
          )}
        </div>
      </div>

      <main className="p-6 overflow-auto">
        <h1 className="text-3xl font-bold mb-2">
          {activeMarket} {activeSide} Support Scanner
        </h1>

        <p className="text-slate-400 mb-6">
          Scans only {activeMarket} {activeSide} strikes with premium Rs.5-Rs.100.
        </p>

          <p className="text-cyan-300 text-sm mb-4">
            Auto refresh every {supportRefreshSeconds} seconds
          </p>

        <div className="bg-[#0b1628] border border-slate-700 rounded-xl p-3 mb-5 grid grid-cols-5 gap-2 items-center">
          <input
            value={clientId}
            onChange={e => setClientId(e.target.value)}
            placeholder="Client ID"
            className="bg-[#111f33] border border-slate-700 rounded px-2 py-1.5 text-sm"
          />

          <input
            value={accessToken}
            onChange={e => setAccessToken(e.target.value)}
            placeholder="Access Token"
            type="password"
            className="bg-[#111f33] border border-slate-700 rounded px-2 py-1.5 text-sm"
          />

          <select
            value={interval}
            onChange={e => setIntervalValue(e.target.value)}
            className="bg-[#111f33] border border-slate-700 rounded px-2 py-1.5 text-sm"
          >
            <option value="1">1 Min</option>
            <option value="5">5 Min</option>
          </select>

         <select
            value={premiumBatch}
            onChange={e => setPremiumBatch(e.target.value)}
            className="bg-[#111f33] border border-slate-700 rounded px-2 py-1.5 text-sm"
          >
            <option value="first">100-50 First 7</option>
            <option value="remaining">50-5 Remaining</option>
          </select>

          <button
            onClick={toggleAutoScan}
            disabled={loading && !runningBySide[activeTab]}
            className={
              runningBySide[activeTab]
                ? "bg-red-500 hover:bg-red-400 rounded font-semibold text-sm px-3 py-1.5"
                : "bg-blue-500 hover:bg-blue-400 disabled:opacity-50 rounded font-semibold text-sm px-3 py-1.5"
            }
          >
            {runningBySide[activeTab]
              ? `Stop ${activeMarket} ${activeSide}`
              : loading
              ? "Starting..."
              : `Scan ${activeMarket} ${activeSide}`}
          </button>

        </div>

        {error && <div className="text-red-300 mb-4">{error}</div>}

        {data && (
          <>
            <div className="mb-4 text-sm text-slate-300">
              Trade Date: <b>{data.tradeDate}</b> | Spot: <b>{Number(data.spot || 0).toFixed(2)}</b> | Expiry: <b>{data.expiry}</b> | Market Bias: <b>{data.marketBias}</b> | Updated:{" "}
              {new Date(data.updatedAt).toLocaleTimeString("en-IN")}
            </div>

            <div className="mb-4 flex flex-wrap items-center gap-4 text-sm">
              <span className="text-slate-400 font-semibold">Filter:</span>

              <label className="flex items-center gap-2 text-green-300">
                <input
                  type="checkbox"
                  checked={statusFilters.buyNow}
                  onChange={() => toggleStatusFilter("buyNow")}
                />
                Buy Now
              </label>

              <label className="flex items-center gap-2 text-yellow-300">
                <input
                  type="checkbox"
                  checked={statusFilters.near}
                  onChange={() => toggleStatusFilter("near")}
                />
                Near SZ
              </label>

              <label className="flex items-center gap-2 text-red-300">
                <input
                  type="checkbox"
                  checked={statusFilters.away}
                  onChange={() => toggleStatusFilter("away")}
                />
                Away SZ
              </label>
            </div>

            <div>
              <h2 className={activeSide === "CE" ? "text-xl font-bold mb-3 text-green-300" : "text-xl font-bold mb-3 text-pink-300"}>
                {activeMarket} {activeSide} Supports
              </h2>

              {renderRows(rows)}
            </div>
          </>
        )}
      </main>
    </div>
  );
}
